/**
 * TaskSummaryWidget - 태스크 요약 위젯
 * DevTeam과 통합된 태스크 현황을 요약 표시
 * 상세 관리는 devteam.ilouli.com/managing으로 연결
 */

import React, { useState, useEffect, useCallback } from 'react';
import { getStats } from '../../services/managingService';

const DEVTEAM_URL = 'https://devteam.ilouli.com/managing';

const TaskSummaryWidget = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadStats = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getStats();
      setStats(data);
    } catch (err) {
      console.error('Failed to load stats:', err);
      setError('통계를 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  const handleOpenDevTeam = () => {
    window.open(DEVTEAM_URL, '_blank', 'noopener,noreferrer');
  };

  if (loading) {
    return (
      <div className="bg-white border border-slate-200 rounded-xl p-6 max-w-[600px]">
        <div className="flex items-center justify-center gap-2 py-10 text-gray-500">
          <div className="w-4 h-4 border-2 border-slate-200 border-t-blue-500 rounded-full animate-spin"></div>
          <span>로딩 중...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white border border-slate-200 rounded-xl p-6 max-w-[600px]">
        <div className="flex flex-col items-center gap-2 py-6 text-gray-500">
          <span className="text-2xl">⚠️</span>
          <span>{error}</span>
          <button onClick={loadStats} className="bg-blue-500 text-white border-none py-1.5 px-3 rounded-md text-xs cursor-pointer mt-2 hover:opacity-90">다시 시도</button>
        </div>
      </div>
    );
  }

  const todoCount = stats?.['진행예정'] || stats?.byStatus?.todo || 0;
  const doingCount = stats?.['진행중'] || stats?.byStatus?.doing || 0;
  const doneCount = stats?.['진행완료'] || stats?.byStatus?.done || 0;
  const total = todoCount + doingCount + doneCount;

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-6 max-w-[600px]">
      {/* Header */}
      <div className="flex items-center justify-between mb-5 max-[480px]:flex-col max-[480px]:items-start max-[480px]:gap-3">
        <div className="flex items-center gap-2">
          <span className="text-xl">📁</span>
          <h3 className="m-0 text-base font-semibold text-gray-900">태스크 현황</h3>
        </div>
        <button className="bg-none border-none text-blue-500 text-[13px] font-medium cursor-pointer py-1.5 px-3 rounded-md transition-colors duration-150 hover:bg-blue-500/10" onClick={handleOpenDevTeam}>
          DevTeam에서 관리 →
        </button>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-4 gap-3 mb-5 max-[480px]:grid-cols-2">
        <div className="text-center py-4 px-3 rounded-lg bg-slate-100">
          <span className="block text-2xl font-bold text-gray-900 leading-tight">{total}</span>
          <span className="block text-xs text-gray-500 mt-1">전체</span>
        </div>
        <div className="text-center py-4 px-3 rounded-lg bg-slate-50 border-l-[3px] border-l-slate-400">
          <span className="block text-2xl font-bold text-gray-900 leading-tight">{todoCount}</span>
          <span className="block text-xs text-gray-500 mt-1">진행예정</span>
        </div>
        <div className="text-center py-4 px-3 rounded-lg bg-blue-50 border-l-[3px] border-l-blue-500">
          <span className="block text-2xl font-bold text-gray-900 leading-tight">{doingCount}</span>
          <span className="block text-xs text-gray-500 mt-1">진행중</span>
        </div>
        <div className="text-center py-4 px-3 rounded-lg bg-emerald-50 border-l-[3px] border-l-emerald-500">
          <span className="block text-2xl font-bold text-gray-900 leading-tight">{doneCount}</span>
          <span className="block text-xs text-gray-500 mt-1">완료</span>
        </div>
      </div>

      {/* Progress Bar */}
      {total > 0 && (
        <div className="mb-4">
          <div className="flex h-2 rounded bg-slate-200 overflow-hidden">
            <div
              className="bg-emerald-500 transition-[width] duration-300"
              style={{ width: `${(doneCount / total) * 100}%` }}
              title={`완료: ${doneCount}`}
            />
            <div
              className="bg-blue-500 transition-[width] duration-300"
              style={{ width: `${(doingCount / total) * 100}%` }}
              title={`진행중: ${doingCount}`}
            />
            <div
              className="bg-slate-400 transition-[width] duration-300"
              style={{ width: `${(todoCount / total) * 100}%` }}
              title={`진행예정: ${todoCount}`}
            />
          </div>
          <div className="flex justify-end mt-1.5">
            <span className="text-xs text-gray-500">완료율: {total > 0 ? Math.round((doneCount / total) * 100) : 0}%</span>
          </div>
        </div>
      )}

      {/* Info */}
      <div className="border-t border-slate-200 pt-4">
        <p className="m-0 text-[13px] text-gray-400">
          상세 태스크 관리, Export, 동기화 기능은
          <a href={DEVTEAM_URL} target="_blank" rel="noopener noreferrer" className="text-blue-500 no-underline font-medium hover:underline"> DevTeam</a>
          에서 이용하세요.
        </p>
      </div>
    </div>
  );
};

export default TaskSummaryWidget;
